import { useState, type CSSProperties } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import type { RfmItem, RfmTemplateItem } from "@/types/rfm";
import { segmentIcons } from "./rfmSegmentIcons";

type RfmSegmentBreakdownProps = {
  items: RfmItem[];
  template: RfmTemplateItem[];
  selectedSegmentId: number | null;
  onSelectSegment: (segmentId: number) => void;
};

const number = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const segmentColours: Record<string, string> = {
  Critical: "#fb7185",
  Loyalist: "#8b5cf6",
  Champion: "#3b82f6",
  "At risk": "#fb923c",
  Average: "#fcd34d",
  "Potential loyalist": "#2dd4bf",
  Hibernating: "#a1a1aa",
  "About to sleep": "#0ea5e9",
  Promising: "#f472b6",
  New: "#a3e635",
};

const defaultSegmentColour = "#94a3b8";

type SortKey = "segmentName" | "companyCount" | "percentageOfCompanies" | "segmentTotalValue1" | "percentageOfTotalValue1";
type SortDirection = "asc" | "desc";

const columns: { key: SortKey; label: string }[] = [
  { key: "segmentName", label: "Segment" },
  { key: "companyCount", label: "Companies" },
  { key: "percentageOfCompanies", label: "% of companies" },
  { key: "segmentTotalValue1", label: "Total sales" },
  { key: "percentageOfTotalValue1", label: "% of sales" },
];

const getCellStyle = (segmentName: string, position: "start" | "middle" | "end", selected: boolean): CSSProperties => {
  const colour = segmentColours[segmentName] ?? defaultSegmentColour;
  const border = `1px solid ${selected ? colour : "transparent"}`;
  return {
    ...(selected && { backgroundColor: `color-mix(in srgb, ${colour} 20%, white)` }),
    borderTop: border,
    borderBottom: border,
    ...(position === "start" && { borderLeft: border }),
    ...(position === "end" && { borderRight: border }),
  };
};


export const RfmSegmentBreakdown = ({ items, template, selectedSegmentId, onSelectSegment }: RfmSegmentBreakdownProps) => {
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection } | null>(null);
  const totalCompanies = items.reduce((total, item) => total + item.companyCount, 0);
  const itemBySegmentId = new Map(items.map(item => [item.segmentId, item]));
  const templateRows: RfmItem[] = template.map(segment => itemBySegmentId.get(segment.segmentId) ?? {
    segmentId: segment.segmentId,
    companyCount: 0,
    percentageOfCompanies: 0,
    segmentTotalValue1: 0,
    percentageOfTotalValue1: 0,
  });
  const getSegmentName = (segmentId: number) => template.find(segment => segment.segmentId === segmentId)?.segmentName ?? `Segment ${segmentId}`;
  const rows = sort === null ? templateRows : [...templateRows].sort((a, b) => {
    const result = sort.key === "segmentName"
      ? getSegmentName(a.segmentId).localeCompare(getSegmentName(b.segmentId))
      : a[sort.key] - b[sort.key];
    return sort.direction === "asc" ? result : -result;
  });

  const handleSort = (key: SortKey) => {
    setSort(current => current?.key === key ? { key, direction: current.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" });
  };

  return (
    <section aria-labelledby="rfm-breakdown-title" className="flex h-full flex-col rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 id="rfm-breakdown-title" className="text-xl font-semibold text-slate-800">Segment breakdown</h2>
      <div className="mt-6 overflow-x-auto">
        <table className="-mt-1 w-full min-w-[620px] border-separate border-spacing-y-1 text-sm tabular-nums">
          <thead>
            <tr>
              {columns.map(column => {
                const active = sort?.key === column.key;
                const alignRight = column.key !== "segmentName";
                return (
                  <th key={column.key} aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"} className={`px-4 pb-2 pt-0 font-semibold text-slate-700 ${alignRight ? "text-right" : "text-left"}`}>
                    <button type="button" className={`flex items-center gap-2 hover:underline ${alignRight ? "ml-auto" : ""}`} onClick={() => handleSort(column.key)}>
                      <span>{column.label}</span>
                      {active && <FontAwesomeIcon icon={sort.direction === "asc" ? faSortUp : faSortDown} aria-hidden />}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(item => {
              const selected = selectedSegmentId === item.segmentId;
              const segmentName = getSegmentName(item.segmentId);
              const segmentIcon = segmentIcons[segmentName];

              return (
                <tr key={item.segmentId}>
                  <td style={getCellStyle(segmentName, "start", selected)} className={`px-4 py-1 ${selected ? "rounded-l-lg text-slate-900" : "text-slate-600"}`}>
                    <button type="button" className="flex items-center gap-2 text-left hover:underline" aria-pressed={selected} onClick={() => onSelectSegment(item.segmentId)}>
                      {segmentIcon && <FontAwesomeIcon icon={segmentIcon} className="text-black" fixedWidth aria-hidden />}
                      <span>{segmentName}</span>
                    </button>
                  </td>
                  <td style={getCellStyle(segmentName, "middle", selected)} className={`px-4 py-1 text-right ${selected ? "text-slate-900" : "text-slate-600"}`}>{item.companyCount}</td>
                  <td style={getCellStyle(segmentName, "middle", selected)} className={`px-4 py-1 text-right ${selected ? "text-slate-900" : "text-slate-600"}`}>{item.percentageOfCompanies.toFixed(1)}%</td>
                  <td style={getCellStyle(segmentName, "middle", selected)} className={`px-4 py-1 text-right ${selected ? "text-slate-900" : "text-slate-600"}`}>{number.format(item.segmentTotalValue1)}</td>
                  <td style={getCellStyle(segmentName, "end", selected)} className={`rounded-r-lg px-4 py-1 text-right ${selected ? "text-slate-900" : "text-slate-600"}`}>{item.percentageOfTotalValue1.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-4 text-sm text-slate-500">{totalCompanies.toLocaleString()} companies included in this analysis.</p>
      </div>
    </section>
  );
};